import { useEffect } from 'react';

import { Link } from 'react-router-dom';

import { useAppSelector } from 'dux/app/hooks';

import { Box, Button, Text } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { useNpsModal } from 'Contexts/NpsModalContext';
import { isMobileDevice } from 'helpers';

import Spacer from 'Components/Spacer';

import type { availableCountries } from 'constants/countries';
import { productsCategories } from 'constants/products';

import { OrderSuccessPromoCommunication } from 'Blocks/OrderSuccessPromoCommunication';
import CustomerOriginForm from '../Blocks/CustomerOriginForm';
import SelfieUploader from '../Blocks/SelfieUploader';
import SelfieUploaderSuccess from '../Blocks/SelfieUploaderSuccess';

import { getIsCustomProductsOrder } from 'dux/checkoutOrder/selectors';

import BaseScene from './BaseScene';

const StyledBaseScene = styled(BaseScene)`
  height: ${legacyTheme.props.fullHeight};
`;

const DesktopGrid = styled.div`
  display: block;
  width: 100%;
  grid-template-columns: repeat(12, 1fr);
  margin: ${legacyTheme.spacing.s32} 0;

  ${legacyTheme.breakpoints.up('sm')} {
    display: grid;
    align-items: center;
    min-height: calc(${legacyTheme.props.fullHeight} / 2);
    margin: 0;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    padding: 0 ${legacyTheme.spacing.s144};
  }
`;

const OrderInfosBlock = styled.div`
  grid-column: 3/7;
  padding: ${legacyTheme.props.minimumBlockPadding};

  word-wrap: break-word;
`;

const CustomerOriginFormWrapper = styled.div`
  grid-column: 8/12;
  padding: 0 ${legacyTheme.props.minimumBlockPadding};
`;

/* NOTE: The following types are a transcript of the pre-ts migration proptypes.
 * They are certainly partially true but deemed good enough for now.
 * They should be improved whenever needed.
 */
type CustomerOriginState = {
  exists: boolean;
  success: boolean;
};

type CustomerOrigins = {
  isLoading: boolean;
  hasError: boolean;
  data: {
    name: string;
    pubkey: string;
  }[];
};

type User = {
  email: string;
  profile: {
    has_selfies: boolean;
  };
};

type Order = {
  pubkey: string;
  items: Array<{
    pubkey: string;
    quantity: number;
    variant: {
      is_formula: boolean;
    };
  }>;
  formulaset?: {
    categories: (typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE)[];
  };
  is_subscription: boolean;
  is_gift: boolean;
  payment_info?: {
    brand: string;
    name: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  serial_no: string;
  shipping_address: {
    address1: string;
    address2: string;
    city: string;
    country: string;
    countryCode: (typeof availableCountries)[number];
    name: string;
    state: string;
    zipcode: string;
  };
  total_amount: number;
  total_credit?: number;
  total_offer?: number;
  total_gift?: number;
  total_price?: number;
  payment_from_customer: number;
  total_shipping?: number;
  total_subscription?: number;
};

type CheckoutSuccessProps = {
  customerOriginState: CustomerOriginState;
  customerOrigins: CustomerOrigins;
  hasSelfies: boolean;
  goToSelfieApp: () => void;
  onSubmitCustomerOrigin: (customerOrigin: {
    value: string;
    label: string;
    comment?: string;
  }) => void;
  order: Order;
  selfiesSaved: boolean;
  user: User;
};

export const CheckoutSuccess = ({
  customerOriginState,
  customerOrigins,
  goToSelfieApp,
  hasSelfies = false,
  onSubmitCustomerOrigin,
  order,
  selfiesSaved = false,
  user,
}: CheckoutSuccessProps) => {
  const isCustomProductsOrder = useAppSelector(getIsCustomProductsOrder);
  const shouldProposeSelfies =
    false && isMobileDevice() && order.items.some((i) => i.variant.is_formula && i.quantity > 0);

  // In some cases (e.g. gift flow) the shipping address might not exist
  const shippingAddress = order?.shipping_address;

  const ctaContent = isCustomProductsOrder
    ? {
        label: 'Learn about your products',
        to: `/digital-leaflet/${order.pubkey}`,
        dataClick: 'learn-products',
        dataFrom: 'post-purchase-order-details',
      }
    : {
        label: 'View order history',
        to: '/account/history',
        dataClick: 'order-history',
        dataFrom: 'post-purchase-order-details',
      };
  const { openNpsModal } = useNpsModal();

  useEffect(() => {
    if (order.pubkey) {
      openNpsModal({ orderPubkey: order.pubkey });
    }
  }, [order]);

  return (
    <StyledBaseScene>
      <DesktopGrid>
        <OrderInfosBlock>
          {shouldProposeSelfies && !user.profile.has_selfies && !hasSelfies && (
            <SelfieUploader goToSelfieApp={goToSelfieApp} />
          )}

          {shouldProposeSelfies && selfiesSaved && <SelfieUploaderSuccess />}

          <Text
            as="h1"
            color="primary.400"
            data-testid="checkout-success-title"
            textAlign="left"
            variant="heading4"
          >
            You’re all set!
          </Text>
          <br />
          <Box asChild paddingBottom="6x">
            <Text as="p" color="primary.400" textAlign="left" variant="bodyLg">
              Your order number is{' '}
              <span data-testid="checkout-success-order-number">{order.serial_no}</span>. Your order
              confirmation was sent to{' '}
              <span data-testid="checkout-success-email">{user.email}</span>
            </Text>
          </Box>
          {shippingAddress && (
            <Text as="p" color="primary.400" textAlign="left" variant="bodyLg">
              Your order ships to:&nbsp;
              <span data-testid="checkout-success-address">
                {shippingAddress.name && <span>{shippingAddress.name}</span>}
                {shippingAddress.address1 && <span>, {shippingAddress.address1}</span>}
                {shippingAddress.address2 && <span>, {shippingAddress.address2}</span>}
                {shippingAddress.city && <span>, {shippingAddress.city}</span>}
                {shippingAddress.zipcode && <span>, {shippingAddress.zipcode}</span>}
                {shippingAddress.state && <span>, {shippingAddress.state}</span>}
                {shippingAddress.country && <span>, {shippingAddress.country}</span>}
              </span>
            </Text>
          )}
          <Spacer axis="vertical" size={32} />
          <Box __minWidth="215px" __width="fit-content">
            <Button asChild padded size="full" variant="primary">
              <Link
                data-click={ctaContent.dataClick}
                data-from={ctaContent.dataFrom}
                to={ctaContent.to}
              >
                {ctaContent.label}
              </Link>
            </Button>
          </Box>
        </OrderInfosBlock>

        <CustomerOriginFormWrapper>
          {(!customerOriginState.exists ||
            (customerOriginState.exists && customerOriginState.success)) &&
            !customerOrigins.isLoading &&
            customerOrigins.data && (
              <CustomerOriginForm
                customerOrigins={customerOrigins.data}
                onSubmit={onSubmitCustomerOrigin}
                success={customerOriginState.success}
              />
            )}
        </CustomerOriginFormWrapper>
      </DesktopGrid>
      <OrderSuccessPromoCommunication category={order?.formulaset?.categories?.[0]} />
    </StyledBaseScene>
  );
};
